import { BasicCard, basicCardStylesCount, EmptyMessage, Loader, useClassNames, useToast } from '@h2oai/ui-kit';
import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { fsApi } from '../../featurestore/api';
import { CoreService, JobStatus } from '../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import { DashboardResponse } from '../../featurestore/gen/ai/h2o/featurestore/api/v1/DashboardApi.pb';
import { ContentsTitle } from '../../featurestore/models';
import { getErrorMessageBarMessage, hasAPIError } from '../../featurestore/utils';
import { RoutePaths } from '../../pages/Routes';
import {
  ISummaryCardListWidgetClassNames,
  ISummaryCardListWidgetStyles,
  summaryCardListWidgetStylesDefault,
} from './SummaryCardListWidget.styles';

interface SummaryItem {
  id: string;
  name: string;
  count?: string;
  description?: string;
  path?: string;
}

const getDashboardItems = ({ projectNumber, featureSetsNumber, featuresNumber }: DashboardResponse): SummaryItem[] => {
  return [
    {
      id: 'projectNumber',
      name: 'Projects',
      count: projectNumber,
      description: '',
      path: RoutePaths.FEATURESTORE_PROJECTS,
    },
    {
      id: 'featureSetsNumber',
      name: 'Feature Sets',
      count: featureSetsNumber,
      description: '',
      path: RoutePaths.FEATURESTORE_FEATURE_SETS,
    },
    { id: 'featuresNumber', name: 'Features', count: featuresNumber, description: '' },
  ];
};

const getJobCount = (jobsCount: string, hasError: boolean) => {
  return hasError || jobsCount === undefined ? '-' : jobsCount;
};

export const SummaryCardListWidget = () => {
  const [data, setData] = useState<SummaryItem[]>([]);
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const history = useHistory();
  const { addToast } = useToast();
  const load = useCallback(async () => {
    setLoadingMessage(`Loading summary...`);
    const results = await Promise.all(
      [
        fsApi(CoreService.GetDashboard),
        fsApi(CoreService.GetJobsWithStatusCount, {
          statusFilter: JobStatus.Running,
        }),
        fsApi(CoreService.GetJobsWithStatusCount, {
          statusFilter: JobStatus.Failed,
        }),
        fsApi(CoreService.GetJobsWithStatusCount, {
          statusFilter: JobStatus.Pending,
        }),
      ].map((p) => p.catch((e) => e))
    );
    const summaryItems: SummaryItem[] = [];
    results.forEach((d, i) => {
      const hasError = hasAPIError(d);
      if (hasError) {
        addToast(getErrorMessageBarMessage(d, `An error has occurred while loading the summary: `));
      }
      switch (i) {
        case 0:
          summaryItems.push(...getDashboardItems(d));
          break;
        case 1:
          summaryItems.push({
            id: 'myRunningJobs',
            name: 'My Running Jobs',
            count: getJobCount(d.jobsCount, hasError),
            description: '',
            path: `${RoutePaths.FEATURESTORE_JOBS}?jobstab=${ContentsTitle.runningJobs}`,
          });
          break;
        case 2:
          summaryItems.push({
            id: 'myFailedJobs',
            name: 'My Failed Jobs',
            count: getJobCount(d.jobsCount, hasError),
            description: '',
            path: `${RoutePaths.FEATURESTORE_JOBS}?jobstab=${ContentsTitle.failedJobs}`,
          });
          break;
        case 3:
          summaryItems.push({
            id: 'myPendingJobs',
            name: 'My Pending Jobs',
            count: getJobCount(d.jobsCount, hasError),
            description: '',
            path: `${RoutePaths.FEATURESTORE_JOBS}?jobstab=${ContentsTitle.pendingJobs}`,
          });
          break;
      }
    });
    setData(summaryItems);
    setLoadingMessage('');
  }, [addToast]);
  const classNames = useClassNames<ISummaryCardListWidgetStyles, ISummaryCardListWidgetClassNames>(
    'SummaryCardListWidget',
    summaryCardListWidgetStylesDefault
  );
  useEffect(() => {
    load();
  }, []);
  return (
    <div className={classNames.root} data-test="summary-card-list-widget">
      {loadingMessage ? (
        <div className={classNames.loader}>
          <Loader label={loadingMessage} />
        </div>
      ) : data.length ? (
        data.map((item) => (
          <div style={{ minWidth: '219px' }} key={item.id}>
            <BasicCard
              styles={basicCardStylesCount}
              data={item}
              idField="id"
              titleField="name"
              countField="count"
              height="71px"
              descriptionField="description"
              onClick={
                item.path
                  ? () => {
                      if (item.path) history.push(item.path);
                    }
                  : undefined
              }
            />
          </div>
        ))
      ) : (
        <div className={classNames.emptyMessage}>
          <EmptyMessage
            message="You don't have any summary data"
            styles={{
              image: { display: 'none' },
            }}
          />
        </div>
      )}
    </div>
  );
};
